// ─────────────────────────────────────────────────────────────────────────────
// Cartello QR azienda — foglio A4 stampabile con QR code per accesso rapido.
// Pensato per essere plastificato e affisso in produzione.
//
// Export:
//   - CartelloQR        → il foglio A4 in sé (usato anche nella modale)
//   - ModaleStampaQR    → wrapper con overlay + pulsanti Stampa/Chiudi
//
// Note:
//   - Usa la libreria `qrcode` per generare il data URL del QR
//   - Lo stile di stampa è self-contained: niente dipendenze su index.css
//   - Il logo del fornitore viene fetchato da /api/uploads/impostazioni-pubbliche
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { LT } from './tokens';
import { Btn } from './components';

// URL pubblica del sistema (produzione). Il codice azienda si aggiunge come path.
// Leggo dinamicamente la origin così funziona sia in dev che in prod.
function buildAziendaUrl(codice) {
  const code = String(codice || '').toUpperCase().trim();
  const win = typeof window !== 'undefined' ? window : null;
  // In produzione l'origin della pagina è già https://app.labthree.it, quindi
  // il risultato è identico a prima. Le due alternative servono solo come rete
  // di sicurezza se un giorno l'app venisse aperta da un altro indirizzo
  // (anteprima, dominio di collaudo): un QR stampato e plastificato deve
  // puntare al sistema vero, non all'indirizzo da cui è stato generato.
  // In dev (localhost) si resta sull'origin locale, così il cartello si prova
  // senza uscire dalla macchina.
  const origin = (win && win.location.hostname !== 'localhost')
    ? (process.env.REACT_APP_PUBLIC_URL || win.__LT_BACKEND__ || win.location.origin)
    : (win ? win.location.origin : '');
  return code ? `${String(origin).replace(/\/+$/, '')}/${code}` : '';
}

// Costruisce l'URL assoluto per un logo salvato in /uploads/loghi/<file>.
function assoluto(url) {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  // In dev con react-scripts il proxy non copre /uploads, quindi è più sicuro
  // far passare da origin (il server Express statico su 3000 serve /uploads).
  return `${origin}${url.startsWith('/') ? '' : '/'}${url}`;
}

// ── CartelloQR ──────────────────────────────────────────────────────────────
export function CartelloQR({ azienda, fornitore }) {
  const [qrDataUrl, setQrDataUrl] = useState('');
  const codice = String(azienda?.codice || '').toUpperCase().trim();
  const urlAzienda = buildAziendaUrl(codice);

  useEffect(() => {
    let vivo = true;
    if (!urlAzienda) { setQrDataUrl(''); return; }
    QRCode.toDataURL(urlAzienda, {
      errorCorrectionLevel: 'H',
      margin: 1,
      width: 900, // alta risoluzione per stampa nitida
      color: { dark: '#0F172A', light: '#FFFFFF' },
    })
      .then(url => { if (vivo) setQrDataUrl(url); })
      .catch(() => { if (vivo) setQrDataUrl(''); });
    return () => { vivo = false; };
  }, [urlAzienda]);

  const logoAzienda   = assoluto(azienda?.logo);
  const nomeFornitore = fornitore?.fornitore_nome || 'LabThree';

  return (
    <div className="lt-qr-sheet" style={{
      // A4 ratio 1:√2 ≈ 210x297mm. Per preview su schermo uso mm come unità
      // così è già alla dimensione reale su stampa.
      width: '210mm', minHeight: '297mm',
      boxSizing: 'border-box',
      padding: '18mm 16mm',
      background: '#fff',
      color: LT.navy,
      fontFamily: LT.sans,
      display: 'flex', flexDirection: 'column',
      boxShadow: '0 10px 40px rgba(0,0,0,.15)',
      margin: '0 auto',
    }}>
      {/* ── Header: identità azienda compatta a sinistra ─────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '5mm',
        paddingBottom: '6mm', borderBottom: `1.5px solid ${LT.n200}`,
      }}>
        {logoAzienda && (
          <img src={logoAzienda} alt={azienda?.ragione_sociale}
            style={{ height: '16mm', maxWidth: '28mm', objectFit: 'contain' }} />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: '5.5mm', fontWeight: 800, lineHeight: 1.15,
            letterSpacing: '-.01em', color: LT.navy,
          }}>
            {azienda?.ragione_sociale || 'La tua azienda'}
          </div>
          {azienda?.citta && (
            <div style={{ fontSize: '3.5mm', color: LT.n500, marginTop: '1mm' }}>
              {azienda.citta}
            </div>
          )}
        </div>
      </div>

      {/* ── Logo orizzontale applicativo, centrato ──────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        paddingTop: '8mm', paddingBottom: '4mm',
      }}>
        <img
          src={`${process.env.PUBLIC_URL || ''}/brand/logo-orizzontale.svg`}
          alt="LabThree"
          style={{ height: '18mm', width: 'auto', display: 'block' }}
        />
      </div>

      {/* ── Titolo invito ──────────────────────────────────────────────── */}
      <div style={{
        textAlign: 'center',
        marginTop: '2mm', marginBottom: '4mm',
      }}>
        <div style={{
          fontSize: '10mm', fontWeight: 800, lineHeight: 1.1,
          letterSpacing: '-.02em',
        }}>
          Scansiona e accedi
        </div>
        <div style={{ fontSize: '4.5mm', color: LT.n600, marginTop: '2mm' }}>
          Apri la fotocamera del tuo smartphone e inquadra il QR code
        </div>
      </div>

      {/* ── QR ──────────────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '4mm',
      }}>
        <div style={{
          padding: '6mm',
          border: `2mm solid ${LT.navy}`,
          borderRadius: '4mm',
          background: '#fff',
        }}>
          {qrDataUrl ? (
            <img src={qrDataUrl} alt="QR code accesso azienda"
              style={{ width: '95mm', height: '95mm', display: 'block' }} />
          ) : (
            <div style={{
              width: '95mm', height: '95mm',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '4mm', color: LT.n400,
            }}>
              {codice ? 'Generazione QR…' : 'Codice azienda mancante'}
            </div>
          )}
        </div>
      </div>

      {/* ── Link testuale (fallback) ───────────────────────────────────── */}
      {urlAzienda && (
        <div style={{ textAlign: 'center', marginTop: '4mm' }}>
          <div style={{ fontSize: '3.8mm', color: LT.n600 }}>
            Oppure vai al seguente link:
          </div>
          <div style={{
            fontSize: '5.5mm', fontWeight: 700, color: LT.navy,
            fontFamily: LT.mono,
            marginTop: '1.5mm',
            wordBreak: 'break-all',
          }}>
            {urlAzienda.replace(/^https?:\/\//i, '')}
          </div>
        </div>
      )}

      {/* ── Footer ─────────────────────────────────────────────────────── */}
      <div style={{ flex: 1 }} />
      <div style={{
        borderTop: `1.5px solid ${LT.n200}`,
        paddingTop: '5mm',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
        fontSize: '3mm', color: LT.n500,
      }}>
        <div>
          <div style={{ fontWeight: 700, color: LT.n700, fontSize: '3.5mm' }}>
            {azienda?.ragione_sociale}
          </div>
          {azienda?.telefono && <div>{azienda.telefono}</div>}
          {azienda?.email && <div>{azienda.email}</div>}
        </div>
        <div style={{ textAlign: 'right' }}>
          <div>Fornito da</div>
          <div style={{ fontWeight: 700, color: LT.blu, fontSize: '3.5mm' }}>
            {nomeFornitore}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── ModaleStampaQR ──────────────────────────────────────────────────────────
export function ModaleStampaQR({ azienda, onClose }) {
  const [fornitore, setFornitore] = useState(null);
  const overlayRef = useRef(null);

  // Carica i dati del fornitore (logo, nome) dalla route pubblica.
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API || window.location.origin + '/api'}/uploads/impostazioni-pubbliche`)
      .then(r => r.json())
      .then(d => setFornitore(d || {}))
      .catch(() => setFornitore({}));
  }, []);

  // Chiudi con ESC.
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleStampa = () => {
    window.print();
  };

  return (
    <>
      {/* Style di stampa iniettato solo quando la modale è montata.
          Nasconde tutto tranne il foglio, imposta pagina A4 e rimuove margini. */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .lt-qr-print-root, .lt-qr-print-root * { visibility: visible !important; }
          .lt-qr-print-root { position: absolute !important; inset: 0 !important; }
          .lt-qr-print-root .lt-qr-no-print { display: none !important; }
          .lt-qr-sheet {
            box-shadow: none !important;
            margin: 0 !important;
          }
          @page { size: A4 portrait; margin: 0; }
          html, body { background: #fff !important; }
        }
      `}</style>

      <div
        ref={overlayRef}
        className="lt-qr-print-root"
        onClick={e => { if (e.target === overlayRef.current) onClose?.(); }}
        style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(15,23,42,.55)',
          overflowY: 'auto',
          padding: '24px 16px',
          animation: 'lt_overlay_in .18s ease-out',
        }}
      >
        {/* Barra azioni (nascosta in stampa via .lt-qr-no-print) */}
        <div className="lt-qr-no-print" style={{
          maxWidth: '210mm', margin: '0 auto 14px',
          background: '#fff', borderRadius: LT.r.lg,
          padding: '10px 14px', boxShadow: LT.sh2,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, flexWrap: 'wrap',
        }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: LT.navy }}>
              Cartello QR — anteprima A4
            </div>
            <div style={{ fontSize: 12, color: LT.n500, marginTop: 2 }}>
              Stampa su A4, plastifica e appendi in produzione.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn kind="outline" onClick={onClose}>Chiudi</Btn>
            <Btn kind="primary" icon="qr" onClick={handleStampa}>Stampa</Btn>
          </div>
        </div>

        {/* Il foglio A4 vero e proprio */}
        <CartelloQR azienda={azienda} fornitore={fornitore || {}} />
      </div>
    </>
  );
}

export default ModaleStampaQR;
